import { FC, useEffect, useState } from "react";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import { toast } from "sonner";
import { useFormik } from "formik";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import * as yup from "yup";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../atom/atom";
import { Textarea } from "rizzui";
import { CategoryService } from "../../../services/category/categoryService";
import { Category } from "../../../type/entities/category-type";
import ColorInput from "../../../components/forms/ColorInput";

interface CategoryFormProps {    
    setOpenModal: any;
    refetch: () => void;
    category?: Category | null;
    formRef?: any;
}

const schema = yup.object({
    name: yup.string().required('El nombre es obligatorio'),
    description: yup.string().optional(),
    color: yup.string().optional(),
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

const CategoryForm:FC<CategoryFormProps> = ({ setOpenModal, refetch, category, formRef }) => {

    // STATES

    const service = new CategoryService();
    const isEdit = category !== null;
    const [isSearched, setIsSearched] = useState<boolean>(false);
    
    // HOOKS
    
    const [, setIsSubmitting]       = useAtom(formsSubmitAtom);
    const { handleErrors }          = useHandleErrors();
    const { getCompaniesList }      = useCompanies();
    const { userCan }               = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: category?.id ?? '',
            name: category?.name ?? '',
            description: category?.description ?? '',
            color: category?.color ?? '#000000',
            company: category?.company?.id ?? ''
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => { 
            if(!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });


    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UNA NUEVA CATEGORIA
     * @EN CREATE A NEW CATEGORY
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createCategory(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Categoría creada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------
    

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UNA CATEGORIA
     * @EN EDIT A CATEGORY   
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editCategory(values)).getResponseData();   
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Categoría editada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);
    
    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // RENDER

    return (
         <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>

                        <RequiredInput
                            id='name'
                            label="Nombre"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.name}  
                        />

                        <div className="md:col-span-6 flex justify-start flex-col md:mt-0 mt-2 fs-color">
                            <label className="mb-1">Color</label>
                            <ColorInput
                                value={formik.values.color}
                                onChange={(e) => formik.setFieldValue('color', e)}
                            />
                        </div>

                        <Textarea
                            id='description'
                            label='Descripción'
                            rows={4}
                            value={formik.values.description}
                            onChange={(e) => formik.setFieldValue('description', e.target.value)}
                            className="mb-4 md:col-span-12"
                        />

                        {userCan('list', 'company') && (
                            <CustomSelect
                            required
                            isSearchable
                            id={'company'}
                            label="Compañia"
                            containerClassName='md:col-span-12'
                            value={
                                formik.values.company ?
                                { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                            }
                            options={getCompaniesList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('company', e?.value) }
                            }
                            error={formik.errors.company}
                            />
                        )}                    
                    </div>
                </div>
            </form>
        </div>
    );
}

export default CategoryForm;
